import { useState, useRef, useMemo } from 'react';
import { LexiconService } from '@lib/tts/LexiconService';
import { useLexiconStore } from '@store/useLexiconStore';
import { useAudioCommands } from '@app/tts/useAudioCommands';
import type { LexiconRule } from '~types/user-data';
import { Plus, Trash2, Save, X, Download, Upload, ArrowUp, ArrowDown, Play, RefreshCw, CornerDownRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Dialog as UiDialog } from '../ui/Dialog';
import { useReaderUIStore } from '@store/useReaderUIStore';
import { useBookStore } from '@store/useBookStore';
import { LEXICON_SAMPLE_CSV } from '@lib/tts/lexiconSample';
import { LexiconCSV } from '@lib/tts/CsvUtils';
import { exportFile } from '@lib/export';
import { useConfirm } from '../ui/ConfirmDialog';

interface LexiconManagerProps {
    /** Whether the dialog is open. */
    open: boolean;
    /** Callback to change the open state. */
    onOpenChange: (open: boolean) => void;
    /** Initial term to populate the form (e.g. from context menu). */
    initialTerm?: string;
}

/**
 * Dialog for managing pronunciation lexicon rules.
 * Supports adding, editing, deleting, importing/exporting rules, and testing pronunciation.
 *
 * @param props - Component props.
 * @returns The LexiconManager dialog component.
 */
export function LexiconManager({ open, onOpenChange, initialTerm }: LexiconManagerProps) {
    const [editingRule, setEditingRule] = useState<Partial<LexiconRule> | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const lexiconService = LexiconService.getInstance();
    const audio = useAudioCommands();

    const currentBookId = useReaderUIStore(state => state.currentBookId);
    const inventory = useBookStore(state => currentBookId ? state.books[currentBookId] : undefined);
    const bookLanguage = inventory?.language || 'en';
    const [scope, setScope] = useState<'global' | 'book'>('global');
    const [languageFilter, setLanguageFilter] = useState<string>(bookLanguage);

    const [testInput, setTestInput] = useState('');
    const [testOutput, setTestOutput] = useState('');
    const [testTrace, setTestTrace] = useState<{ rule: LexiconRule, before: string, after: string }[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const confirm = useConfirm();

    // Subscribe to store
    const rulesMap = useLexiconStore(state => state.rules);
    const settings = useLexiconStore(state => state.settings);

    const rules = useMemo(() => {
        const list = Object.values(rulesMap || {}).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        let filtered = list;
        if (scope === 'global') {
            filtered = list.filter(r => !r.bookId || r.bookId === 'global');
        } else if (currentBookId) {
            filtered = list.filter(r => r.bookId === currentBookId);
        } else {
            filtered = [];
        }

        if (languageFilter && languageFilter !== 'all') {
            if (languageFilter === 'unscoped') {
                filtered = filtered.filter(r => !r.language);
            } else {
                filtered = filtered.filter(r => r.language === languageFilter || !r.language);
            }
        }
        return filtered;
    }, [rulesMap, scope, currentBookId, languageFilter]);

    const biblePreference = currentBookId ? (settings[currentBookId]?.bibleLexiconEnabled || 'default') : 'default';

    const [initializedTerm, setInitializedTerm] = useState<string | null>(null);

    // Reset initialization tracker when closed
    if (!open && initializedTerm !== null) {
        setInitializedTerm(null);
    }

    // Initialize state when opening with a term
    if (open && initialTerm && initializedTerm !== initialTerm) {
        setIsAdding(true);
        setEditingRule({ original: initialTerm, replacement: '' });
        setTestInput(initialTerm);
        setInitializedTerm(initialTerm);
    }

    const handleBiblePreferenceChange = async (pref: 'on' | 'off' | 'default') => {
        if (currentBookId) {
            await lexiconService.setBibleLexiconPreference(currentBookId, pref);
        }
    };

    const handleSave = async () => {
        if (!editingRule?.original || !editingRule?.replacement) return;

        await lexiconService.saveRule({
            id: editingRule.id,
            original: editingRule.original,
            replacement: editingRule.replacement,
            isRegex: editingRule.isRegex,
            matchType: editingRule.matchType || (editingRule.isRegex ? 'regex' : 'ignore_case'),
            bookId: scope === 'book' ? (currentBookId || undefined) : undefined,
            applyBeforeGlobal: editingRule.applyBeforeGlobal,
            language: editingRule.language
        });

        setIsAdding(false);
        setEditingRule(null);
    };

    const moveRule = async (index: number, direction: 'up' | 'down') => {
        const newRules = [...rules];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newRules.length) return;

        const temp = newRules[index];
        newRules[index] = newRules[targetIndex];
        newRules[targetIndex] = temp;

        // Persist order
        const updates = newRules.map((rule, idx) => ({ id: rule.id, order: idx }));
        await lexiconService.reorderRules(updates);
    };

    const handleDelete = async (id: string) => {
        await lexiconService.deleteRule(id);
    };

    const performReplacement = async (useAllRules: boolean) => {
        let rulesToApply: LexiconRule[] = [];

        if (!useAllRules) {
            // Only use the current rule being edited
            if (editingRule && editingRule.original && editingRule.replacement) {
                rulesToApply = [{
                    id: editingRule.id || 'temp',
                    original: editingRule.original,
                    replacement: editingRule.replacement,
                    isRegex: editingRule.isRegex,
                    matchType: editingRule.matchType || (editingRule.isRegex ? 'regex' : 'ignore_case'),
                    bookId: scope === 'book' ? (currentBookId || undefined) : undefined,
                    applyBeforeGlobal: editingRule.applyBeforeGlobal,
                    language: editingRule.language,
                    created: 0,
                    order: 0
                } as LexiconRule];
            }
        } else {
            // Use all rules (including global/bible if applicable), merging edits
            let baseRules: LexiconRule[] = [];

            if (scope === 'global') {
                baseRules = await lexiconService.getRules();
            } else if (currentBookId) {
                baseRules = await lexiconService.getRules(currentBookId);
            }

            rulesToApply = [...baseRules];

            // Apply editing rule override
            if (editingRule && editingRule.original && editingRule.replacement) {
                const idx = rulesToApply.findIndex(r => r.id === editingRule.id);
                const r = {
                    id: editingRule.id || 'temp',
                    original: editingRule.original,
                    replacement: editingRule.replacement,
                    isRegex: editingRule.isRegex,
                    matchType: editingRule.matchType || (editingRule.isRegex ? 'regex' : 'ignore_case'),
                    bookId: scope === 'book' ? (currentBookId || undefined) : undefined,
                    applyBeforeGlobal: editingRule.applyBeforeGlobal,
                    language: editingRule.language,
                    created: 0,
                    // Preserve order if replacing, else append (simplified)
                    order: idx >= 0 ? rulesToApply[idx].order : rulesToApply.length
                } as LexiconRule;

                if (idx >= 0) {
                    rulesToApply[idx] = r;
                } else {
                    // Append new rules to the end for testing purposes.
                    // In production, LexiconService handles sorting/priority.
                    rulesToApply.push(r);
                }
            }
        }

        const { final, trace } = lexiconService.applyLexiconWithTrace(testInput, rulesToApply);
        setTestOutput(final);
        setTestTrace(trace);
        return final;
    };

    const handleReplaceCurrent = async () => { await performReplacement(false); };
    const handleReplaceAll = async () => { await performReplacement(true); };

    const handlePlay = async () => {
        let textToPlay = testOutput;
        // If output is empty, replace using all rules first
        if (!textToPlay) {
            textToPlay = await performReplacement(true);
        }

        if (textToPlay) {
            audio.preview(textToPlay);
        }
    };

    const handleDownloadSample = async () => {
        await exportFile({
            filename: 'lexicon_sample.csv',
            data: LEXICON_SAMPLE_CSV,
            mimeType: 'text/csv'
        });
    };

    const handleExport = async () => {
        const csv = LexiconCSV.generate(rules);
        const filename = `lexicon_${scope}${scope === 'book' ? '_' + currentBookId : ''}.csv`;
        await exportFile({
            filename,
            data: csv,
            mimeType: 'text/csv'
        });
    };

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            const text = evt.target?.result as string;
            if (text) {
                const newRules = LexiconCSV.parse(text);

                // Delete existing rules for the current scope before importing
                let rulesToDelete: LexiconRule[] = [];
                if (scope === 'global') {
                    rulesToDelete = await lexiconService.getRules();
                } else if (currentBookId) {
                    const all = await lexiconService.getRules(currentBookId);
                    rulesToDelete = all.filter(r => r.bookId === currentBookId);
                }

                const proceed = await confirm({
                    titleKey: 'lexicon.import.replace.title',
                    bodyKey: 'lexicon.import.replace.body',
                    params: { oldCount: rulesToDelete.length, newCount: newRules.length },
                    confirmKey: 'common.continue',
                });
                if (!proceed) {
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }

                if (rulesToDelete.length > 0) {
                    await lexiconService.deleteRules(rulesToDelete.map(r => r.id));
                }

                for (const r of newRules) {
                    await lexiconService.saveRule({
                        original: r.original,
                        replacement: r.replacement,
                        isRegex: r.isRegex,
                        matchType: r.matchType,
                        bookId: scope === 'book' ? (currentBookId || undefined) : undefined,
                        applyBeforeGlobal: r.applyBeforeGlobal,
                        language: r.language
                    });
                }
            }
            // Reset input
            if (fileInputRef.current) fileInputRef.current.value = '';
        };
        reader.readAsText(file);
    };

    return (
        <UiDialog
            isOpen={open}
            onClose={() => onOpenChange(false)}
            title="Pronunciation Lexicon"
            description="Define custom pronunciation rules for specific words or names."
            footer={
                <Button data-testid="lexicon-close-btn" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            }
        >
            <div className="flex justify-between items-center mb-4 border-b border-border pb-2 overflow-x-auto gap-4">
                <div className="flex items-center gap-4 shrink-0">
                    <div className="flex space-x-4" role="tablist" aria-label="Lexicon Scope">
                    <button
                        role="tab"
                        aria-selected={scope === 'global'}
                        onClick={() => setScope('global')}
                        className={`pb-1 px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded ${scope === 'global' ? 'border-b-2 border-primary font-bold text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                        Global
                    </button>
                    {currentBookId && (
                        <button
                            role="tab"
                            aria-selected={scope === 'book'}
                            onClick={() => setScope('book')}
                            className={`pb-1 px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded ${scope === 'book' ? 'border-b-2 border-primary font-bold text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            This Book
                        </button>
                    )}
                    </div>
                    <select
                        className="h-8 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        value={languageFilter}
                        onChange={(e) => setLanguageFilter(e.target.value)}
                        aria-label="Filter by Language"
                    >
                        <option value="all">All Languages</option>
                        <option value="en">English</option>
                        <option value="zh">Chinese</option>
                        <option value="unscoped">Unscoped (Legacy)</option>
                    </select>
                </div>

                <div className="flex gap-2 shrink-0">
                    <Button data-testid="lexicon-download-sample" variant="ghost" size="sm" onClick={handleDownloadSample} title="Download Sample CSV">
                        <Download size={14} className="mr-1" /> Sample
                    </Button>
                    <Button data-testid="lexicon-export" variant="ghost" size="sm" onClick={handleExport} title="Export to CSV">
                        <Download size={14} className="mr-1" /> Export
                    </Button>
                    <Button data-testid="lexicon-import-btn" asChild variant="ghost" size="sm" title="Import from CSV">
                        <label className="cursor-pointer flex items-center">
                            <Upload size={14} className="mr-1" /> Import
                            <input data-testid="lexicon-import-input" ref={fileInputRef} type="file" className="hidden" accept=".csv" onChange={handleImport} />
                        </label>
                    </Button>
                </div>
            </div>

            <div data-testid="lexicon-list-container" className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                {scope === 'book' && currentBookId && (
                    <div className="mb-4 p-3 bg-muted/20 border rounded flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                            <div>
                                <h4 className="text-sm font-semibold">Bible Abbreviations & Lexicon</h4>
                                <p className="text-xs text-muted-foreground">Override global setting for this book.</p>
                            </div>
                            <div className="flex bg-muted rounded p-1" role="group" aria-label="Bible Lexicon Preference">
                                <button
                                    type="button"
                                    aria-pressed={biblePreference === 'default'}
                                    data-testid="lexicon-pref-default"
                                    onClick={() => handleBiblePreferenceChange('default')}
                                    className={`px-3 py-1 text-xs rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${biblePreference === 'default' ? 'bg-background shadow-sm font-medium' : 'hover:bg-background/50 text-muted-foreground'}`}
                                >
                                    Default
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={biblePreference === 'on'}
                                    data-testid="lexicon-pref-on"
                                    onClick={() => handleBiblePreferenceChange('on')}
                                    className={`px-3 py-1 text-xs rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${biblePreference === 'on' ? 'bg-background shadow-sm font-medium text-green-600' : 'hover:bg-background/50 text-muted-foreground'}`}
                                >
                                    On
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={biblePreference === 'off'}
                                    data-testid="lexicon-pref-off"
                                    onClick={() => handleBiblePreferenceChange('off')}
                                    className={`px-3 py-1 text-xs rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${biblePreference === 'off' ? 'bg-background shadow-sm font-medium text-destructive' : 'hover:bg-background/50 text-muted-foreground'}`}
                                >
                                    Off
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* List */}
                <div data-testid="lexicon-rules-list" className="space-y-2">
                    {rules.map((rule, index) => (
                        <div key={rule.id} className="flex items-center justify-between p-2 bg-card rounded border border-border">
                            {editingRule?.id === rule.id ? (
                                <div className="flex flex-1 items-center gap-2">
                                    <div className="flex flex-col gap-1 w-full">
                                        <div className="flex flex-col gap-2 w-full">
                                            <input
                                                data-testid="lexicon-input-original"
                                                aria-label="Original text"
                                                className="border p-1 rounded flex-1 min-w-0 text-sm bg-background border-input text-foreground"
                                                value={editingRule.original}
                                                onChange={e => setEditingRule({ ...editingRule, original: e.target.value })}
                                                placeholder="Original"
                                            />
                                            <div className="flex items-center gap-2 w-full">
                                                <CornerDownRight size={16} className="text-muted-foreground shrink-0" />
                                                <input
                                                    data-testid="lexicon-input-replacement"
                                                    aria-label="Replacement text"
                                                    className="border p-1 rounded flex-1 min-w-0 text-sm bg-background border-input text-foreground"
                                                    value={editingRule.replacement}
                                                    onChange={e => setEditingRule({ ...editingRule, replacement: e.target.value })}
                                                    placeholder="Replacement"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                                            <div className="flex items-center gap-4 flex-wrap">
                                                <div className="flex items-center gap-2">
                                                    <label className="text-xs font-medium text-muted-foreground">Lang:</label>
                                                    <select
                                                        aria-label="Rule language"
                                                        data-testid="lexicon-rule-language-select"
                                                        className="h-7 text-xs rounded border border-input bg-transparent px-2 py-1"
                                                        value={editingRule.language || ''}
                                                        onChange={e => setEditingRule({ ...editingRule, language: e.target.value || undefined })}
                                                    >
                                                        <option value="">All</option>
                                                        <option value="en">en</option>
                                                        <option value="zh">zh</option>
                                                    </select>
                                                </div>
                                                <select
                                                    data-testid="lexicon-match-type-select"
                                                    value={editingRule.matchType || (editingRule.isRegex ? 'regex' : 'ignore_case')}
                                                    onChange={e => {
                                                        const matchType = e.target.value as 'ignore_case' | 'match_case' | 'regex';
                                                        setEditingRule({ ...editingRule, matchType, isRegex: matchType === 'regex' });
                                                    }}
                                                    className="rounded border-input text-xs bg-background h-6 text-muted-foreground"
                                                >
                                                    <option value="ignore_case">Ignore Case</option>
                                                    <option value="match_case">Match Case</option>
                                                    <option value="regex">Regular Expression</option>
                                                </select>
                                                {scope === 'book' && (
                                                    <label className="flex items-center gap-2 text-xs text-muted-foreground" title="Apply this rule before global rules">
                                                        <input
                                                            data-testid="lexicon-priority-checkbox"
                                                            aria-label="Apply this rule before global rules"
                                                            type="checkbox"
                                                            checked={editingRule.applyBeforeGlobal || false}
                                                            onChange={e => setEditingRule({ ...editingRule, applyBeforeGlobal: e.target.checked })}
                                                            className="rounded border-input"
                                                        />
                                                        High Priority
                                                    </label>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <Button variant="ghost" size="icon" aria-label="Save rule" data-testid="lexicon-save-rule-btn" onClick={handleSave} className="h-7 w-7 text-green-600 hover:bg-green-500/10 hover:text-green-600"><Save size={18} /></Button>
                                                <Button variant="ghost" size="icon" aria-label="Cancel editing" data-testid="lexicon-cancel-rule-btn" onClick={() => setEditingRule(null)} className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"><X size={18} /></Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex-1 flex flex-col gap-1 min-w-0 py-1">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            {(rule.matchType === 'regex' || (!rule.matchType && rule.isRegex)) && <span data-testid="lexicon-regex-badge" className="text-[10px] uppercase font-bold text-purple-600 border border-purple-200 bg-purple-50 px-1 rounded shrink-0">Re</span>}
                                            {rule.matchType === 'match_case' && <span data-testid="lexicon-match-case-badge" className="text-[10px] uppercase font-bold text-blue-600 border border-blue-200 bg-blue-50 px-1 rounded shrink-0">Aa</span>}
                                            {rule.applyBeforeGlobal && <span data-testid="lexicon-priority-badge" className="text-[10px] uppercase font-bold text-orange-600 border border-orange-200 bg-orange-50 px-1 rounded shrink-0">Pre</span>}
                                            <span className="font-mono text-sm break-all text-foreground">{rule.original}</span>
                                        </div>
                                        <div className="flex items-center gap-2 pl-1">
                                            <CornerDownRight size={14} className="text-muted-foreground shrink-0" />
                                            <span className="font-semibold text-foreground/80 text-sm break-words">{rule.replacement}</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <div className="flex flex-col mr-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label="Move rule up"
                                                data-testid={`lexicon-move-up-${index}`}
                                                onClick={() => moveRule(index, 'up')}
                                                disabled={index === 0}
                                                className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                            >
                                                <ArrowUp size={12} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label="Move rule down"
                                                data-testid={`lexicon-move-down-${index}`}
                                                onClick={() => moveRule(index, 'down')}
                                                disabled={index === rules.length - 1}
                                                className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                            >
                                                <ArrowDown size={12} />
                                            </Button>
                                        </div>
                                        <Button variant="ghost" size="sm" aria-label="Edit rule" onClick={() => setEditingRule({ ...rule, matchType: rule.matchType || (rule.isRegex ? 'regex' : 'ignore_case') })} className="h-7 px-2 text-xs text-primary hover:text-primary hover:bg-primary/10">Edit</Button>
                                        <Button variant="ghost" size="icon" aria-label="Delete rule" onClick={() => handleDelete(rule.id)} className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"><Trash2 size={16} /></Button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}

                    {rules.length === 0 && !isAdding && (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                            No rules defined for this scope.
                        </div>
                    )}
                </div>

                {/* Add New */}
                {isAdding ? (
                    <div className="flex flex-col gap-2 p-2 border rounded bg-card border-border">
                        <div className="flex flex-col gap-2">
                            <input
                                data-testid="lexicon-input-original"
                                aria-label="Original text"
                                className="border p-1 rounded flex-1 min-w-0 text-sm bg-background border-input text-foreground"
                                value={editingRule?.original || ''}
                                onChange={e => setEditingRule({ ...editingRule, original: e.target.value })}
                                placeholder="Original"
                                autoFocus
                            />
                            <div className="flex items-center gap-2">
                                <CornerDownRight size={16} className="text-muted-foreground shrink-0" />
                                <input
                                    data-testid="lexicon-input-replacement"
                                    aria-label="Replacement text"
                                    className="border p-1 rounded flex-1 min-w-0 text-sm bg-background border-input text-foreground"
                                    value={editingRule?.replacement || ''}
                                    onChange={e => setEditingRule({ ...editingRule, replacement: e.target.value })}
                                    placeholder="Replacement"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                            <div className="flex items-center gap-4 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-muted-foreground">Lang:</label>
                                    <select
                                        aria-label="Rule language"
                                        data-testid="lexicon-rule-language-select"
                                        className="h-7 text-xs rounded border border-input bg-transparent px-2 py-1"
                                        value={editingRule?.language || ''}
                                        onChange={e => setEditingRule({ ...editingRule, language: e.target.value || undefined })}
                                    >
                                        <option value="">All</option>
                                        <option value="en">en</option>
                                        <option value="zh">zh</option>
                                    </select>
                                </div>
                                <select
                                    data-testid="lexicon-match-type-select"
                                    value={editingRule?.matchType || (editingRule?.isRegex ? 'regex' : 'ignore_case')}
                                    onChange={e => {
                                        if (editingRule) {
                                            const matchType = e.target.value as 'ignore_case' | 'match_case' | 'regex';
                                            setEditingRule({ ...editingRule, matchType, isRegex: matchType === 'regex' });
                                        }
                                    }}
                                    className="rounded border-input text-xs bg-background h-6 text-muted-foreground"
                                >
                                    <option value="ignore_case">Ignore Case</option>
                                    <option value="match_case">Match Case</option>
                                    <option value="regex">Regular Expression</option>
                                </select>
                                {scope === 'book' && (
                                    <label className="flex items-center gap-2 text-xs text-muted-foreground" title="Apply this rule before global rules">
                                        <input
                                            data-testid="lexicon-priority-checkbox"
                                            aria-label="Apply this rule before global rules"
                                            type="checkbox"
                                            checked={editingRule?.applyBeforeGlobal || false}
                                            onChange={e => setEditingRule({ ...editingRule, applyBeforeGlobal: e.target.checked })}
                                            className="rounded border-input"
                                        />
                                        High Priority
                                    </label>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button variant="ghost" size="icon" aria-label="Save rule" data-testid="lexicon-save-rule-btn" onClick={handleSave} className="h-7 w-7 text-green-600 hover:bg-green-500/10 hover:text-green-600"><Save size={18} /></Button>
                                <Button variant="ghost" size="icon" aria-label="Cancel adding" data-testid="lexicon-cancel-rule-btn" onClick={() => { setIsAdding(false); setEditingRule(null); }} className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"><X size={18} /></Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <Button data-testid="lexicon-add-rule-btn" onClick={() => { setIsAdding(true); setEditingRule({}); }} variant="outline" className="w-full flex items-center justify-center gap-2 text-sm">
                        <Plus size={16} /> Add Rule
                    </Button>
                )}

                {/* Test Area */}
                <div className="mt-8 pt-4 border-t border-border">
                    <h4 className="text-xs font-semibold mb-2 text-muted-foreground uppercase">Test Pronunciation</h4>
                    <div className="flex flex-col gap-2">
                        <input
                            data-testid="lexicon-test-input"
                            aria-label="Test sentence"
                            className="w-full border p-2 rounded text-sm bg-background border-input text-foreground"
                            placeholder="Type a sentence containing your words..."
                            value={testInput}
                            onChange={e => setTestInput(e.target.value)}
                        />
                        <div className="flex flex-wrap gap-2">
                            <Button
                                data-testid="lexicon-test-current-btn"
                                onClick={handleReplaceCurrent}
                                variant="outline"
                                size="sm"
                                disabled={!editingRule}
                                title="Replace using only the current rule being edited"
                            >
                                <RefreshCw size={14} className="mr-2" /> Current
                            </Button>
                            <Button
                                data-testid="lexicon-test-all-btn"
                                onClick={handleReplaceAll}
                                variant="outline"
                                size="sm"
                                title="Replace using all rules (including current edits)"
                            >
                                <RefreshCw size={14} className="mr-2" /> All Rules
                            </Button>
                            <Button
                                data-testid="lexicon-play-btn"
                                onClick={handlePlay}
                                variant="outline"
                                size="sm"
                                title="Play the processed text"
                            >
                                <Play size={14} className="mr-2" /> Play
                            </Button>
                        </div>
                    </div>
                    {testOutput && (
                        <div className="mt-2 text-sm text-muted-foreground">
                            Processed: <span className="italic text-foreground">{testOutput}</span>
                        </div>
                    )}

                    {testTrace.length > 0 && (
                        <div className="mt-4 flex flex-col gap-2">
                            <h5 className="text-xs font-semibold text-muted-foreground uppercase">Transformation Steps</h5>
                            <div className="border rounded border-border bg-muted/30 divide-y divide-border text-xs max-h-[200px] overflow-y-auto">
                                {testTrace.map((item, idx) => {
                                    const isBible = item.rule.id.startsWith('bible-');
                                    return (
                                        <div key={idx} className="p-2 flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                {isBible ? (
                                                    <span className="bg-primary/10 text-primary px-1 rounded text-[10px] uppercase font-bold shrink-0">Bible</span>
                                                ) : (
                                                    <span className="bg-muted text-muted-foreground px-1 rounded text-[10px] uppercase font-bold shrink-0">Rule</span>
                                                )}

                                                {isBible ? (
                                                    <span className="font-mono text-muted-foreground truncate">
                                                        {item.rule.original} &rarr; {item.rule.replacement}
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            const found = rules.find(r => r.id === item.rule.id);
                                                            if (found) {
                                                                setEditingRule({ ...found, matchType: found.matchType || (found.isRegex ? 'regex' : 'ignore_case') });
                                                                setIsAdding(false);
                                                            }
                                                        }}
                                                        className="font-mono font-semibold text-primary hover:underline truncate text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
                                                        title="Click to edit rule"
                                                    >
                                                        {item.rule.original} &rarr; {item.rule.replacement}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="pl-2 border-l-2 border-border ml-1">
                                                <div className="text-muted-foreground truncate">
                                                    <span className="italic text-foreground/90">{item.after}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </UiDialog>
    );
}
